import {
  createStore, applyMiddleware, compose, combineReducers
} from 'redux';
import * as thunkModule from 'redux-thunk';
import { reducersConfiguration } from 'dependencies/reducers';
import { STATE } from 'app/constants';
import { globalVar, isBrowser, isDevelopment } from './universal';
import { log } from './log';
import { typedToPlain } from './redux';
import { textCollectionReducer } from 'app/infrastructure/translate';
import { errorsReducer } from 'app/shell/reducers';
import { crashNotification } from 'app/infrastructure/notifications/crash-notification';
import { logErrors, createSaveMiddleware } from 'app/infrastructure/redux';
import { actionCreators as draftActions } from 'app/area/case/draft/actions';
import { userMessages, reducer as messageReducer } from 'app/infrastructure/notifications/user-messages';
import { ApplicationState } from 'app/types-store';

export function configureStore(): Redux.Store<ApplicationState> {

  const enabledMiddleware = [
    (thunkModule as any).default,
    logErrors,
    typedToPlain,
    userMessages,
    createSaveMiddleware(draftActions.backgroundSave, draftActions.submitDocument, STATE.case)
  ];

  if (isDevelopment) {
    log('Development mode');
    enabledMiddleware.push(require('redux-freeze') as any);
  }

  const middlewares = applyMiddleware(...enabledMiddleware);

  const composer = addDevtoolsIfApplicable(middlewares);

  const createStoreWithMiddleware = composer(createStore);

  const { INITIAL_APP_STATE, reducers } = reducersConfiguration;

  // Combine all reducers and instantiate the app-wide store instance
  const allReducers: Redux.Reducer<any> = buildRootReducer(reducers);

  const store: Redux.Store<ApplicationState> = createStoreWithMiddleware(allReducers, INITIAL_APP_STATE) as Redux.Store<ApplicationState>;

  // Enable Webpack hot module replacement for reducers
  if (module.hot) {
    module.hot.accept('app/types-store', () => {
      const factory = require<any>('app/factory-default');
      store.replaceReducer(buildRootReducer(factory.reducers));
    });
  }

  return store;
}

/**
 * Runs both default reducers, and reducers that operate on full state, for cross-cutting concerns
 */
export function buildRootReducer(allReducers: any) {

  return combineSliceAndGlobalReducers(
    {
      ...allReducers
    },
    [textCollectionReducer, errorsReducer, messageReducer]
  ) as Redux.Reducer<any>;
}

/**
 * Adds redux devtools if abaliable
 *
 * @param middlewares
 */
function addDevtoolsIfApplicable(middlewares: any){

  // Test if redux devTools is avaliable
  const devToolsExtension = globalVar('__REDUX_DEVTOOLS_EXTENSION__', (f: any) => f);

  return ( isBrowser && devToolsExtension() )
        ?
        compose( middlewares, devToolsExtension() ) // Compose with undefined argument fails on safari 9
        :
        middlewares;
}

/**
 * Creates one function composed by default redux reducers that access their own slice of state, and a set of
 * reducers that have access to the full store (ApplicationState)
 */
function combineSliceAndGlobalReducers(keyedReducers: any = {}, reducers: any[]) {

  return (state = {}, action: any) => {

    let result: any = Object.assign({}, state);

    try{
      // assert for any errors, but don't use the result
      if (isDevelopment) {
        combineReducers(keyedReducers);
      }
      // run reducers that are specific to top-level keys
      Object.keys(keyedReducers).reduce((prev: any, key: any) => {
        const reducer = keyedReducers[key];
        result[key] = reducer(result[key], action);
      }, result);

      // run reducers that access the complete state
      reducers.reduce((prev, reducer) => {
        result = reducer(result, action);
      }, result);

    } catch (err) {
        log('A reducer crashed, entering fatal state');
        console.error(err);

        crashNotification('We are sorry! Synergi Life has stopped working.', err.message);
    }

    return result;
  };
}
